import { useMemo } from "react";
import { useSelector } from "react-redux";
import { restoreNestedFiles } from "../../../utils/craftFormFiles";

// API file fields come back as { media_id, file_name, media_url, ... } — the
// same shape proposalService.js and the legacy renderer already read
// elsewhere. @craft-form/renderer's file-upload field instead reads
// `fileName`/`name` and `url`/`signedUrl`. Detecting by shape rather than
// hardcoding each file-upload field name (dn_pancard, dn_aadhcard,
// dn_aadhcard_back, dn_mbbs_qual_cert, dn_qualification_cert) means a new file
// field needs no matching change here.
const isApiFileRef = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  typeof value.file_name === "string" &&
  typeof value.media_url === "string";

// Fields whose saved value is a full date ("01-01-2025") but whose select only
// holds/emits a bare year — either a *_YEAR entity dropdown or (as
// dn_pracsinc_1 and dn_yearofhighqual are configured) a client-generated
// yearRange select, whose options carry a NUMBER `value` (e.g. 2025), not a
// string. Without extraction the select silently shows unselected despite
// having a saved value. Derived from the published fields instead of
// hardcoding names so a future *_YEAR/yearRange select is covered for free.
const isGeneratedYearRangeField = (field) =>
  field.dataSourceType === "generated" &&
  field.generatedOptions?.kind === "yearRange";

// Takes the fields array rather than reading it at module load: it now comes
// from the DB, so this has to be recomputed once the blueprint lands. Same
// signature as the admin panel's copy.
const getYearSelectFieldNames = (fields) =>
  new Set(
    (Array.isArray(fields) ? fields : [])
      .filter(
        (field) =>
          field.type === "select" &&
          (field.entityType?.endsWith("_YEAR") ||
            isGeneratedYearRangeField(field)),
      )
      .map((field) => field.name),
  );

// Numeric, not string: matches the generated yearRange options' `value` type
// so the select's strict value === option.value check actually finds the
// option and shows it as selected.
const extractYear = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return value;
  const match = value.match(/(\d{4})/);
  return match ? Number(match[1]) : value;
};

// Inverse of extractYear, for the submit path — the API returns and expects
// these fields as "01-01-<year>", while the select only ever holds/emits the
// bare year. Anything else (an already-formatted date, "", null) is left
// untouched rather than guessed at.
export const denormalizeProposalRecord = (values, fields) => {
  if (!values) return values;

  const yearSelectFieldNames = getYearSelectFieldNames(fields);

  const restoreYear = (value) => {
    if (typeof value === "number" && Number.isInteger(value)) {
      return `01-01-${value}`;
    }
    if (typeof value === "string" && /^\d{4}$/.test(value)) {
      return `01-01-${value}`;
    }
    return value;
  };

  const denormalized = {};
  for (const [key, value] of Object.entries(values)) {
    denormalized[key] = yearSelectFieldNames.has(key)
      ? restoreYear(value)
      : value;
  }
  return denormalized;
};

const toRendererFileRef = (value) =>
  isApiFileRef(value)
    ? { ...value, name: value.file_name, url: value.media_url }
    : value;

// Guards against proposalJson's "" initial state as well as null — react-hook-form's
// `values` must be an object, never a string.
const normalizeProposalRecord = (record, fields) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return {};

  const yearSelectFieldNames = getYearSelectFieldNames(fields);

  const normalized = {};
  for (const [key, value] of Object.entries(record)) {
    if (isApiFileRef(value)) {
      normalized[key] = toRendererFileRef(value);
    } else if (yearSelectFieldNames.has(key)) {
      normalized[key] = extractYear(value);
    } else {
      normalized[key] = value;
    }
  }

  // A relation-table row's document is saved under its full path
  // (`<tableKey>.<rowIndex>.<attributeKey>`) and comes back as a top-level key,
  // since the API merges every media row into the form JSON by attribute key.
  // Put it back inside its row, where the table's file cell reads it.
  return restoreNestedFiles(normalized, toRendererFileRef);
};

// Reads the proposal record the page already fetched via readPrposalData
// (dispatched to buyNow.proposalJson) and reshapes it into what the renderer's
// fields expect, so opening a saved proposal shows its saved values.
const useProposalFormData = () => {
  const proposalJson = useSelector((state) => state.buyNow.proposalJson);
  const fields = useSelector((state) => state.buyNow.ProposalFieldsJSON);

  return useMemo(
    () => normalizeProposalRecord(proposalJson, fields),
    [proposalJson, fields],
  );
};

export default useProposalFormData;
